import { Component, ElementRef, ViewChild, EventEmitter, Input, Output } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable, Subject } from 'rxjs';
import { map, startWith, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { DistinctService, FilterOption } from '@portals/api';

@Component({
	selector: 'portals-multiselect-autocomplete',
	templateUrl: './multiselect-autocomplete.component.html',
	styleUrls: ['./multiselect-autocomplete.component.scss'],
})
export class MultiselectAutocompleteComponent {
	@Input() fieldLabel: string = '';
	@Input() placeholder: string = '';
	@Input() selectable = true;
	@Input() removable = true;
	@Input() selectedData: (FilterOption | string)[] = [];
	@Input() data: (FilterOption | string)[] = [];
	@Input() entityName?: string = '';
	@Input() fieldName?: string = '';
	@Input() filterParams?: object = {};
	@Output() onValueChange = new EventEmitter<{
		value: (FilterOption | string)[];
		isMultiSelect: boolean;
	}>();
	@ViewChild('autocompleteInput')
	autocompleteInput!: ElementRef<HTMLInputElement>;
	@ViewChild('auto') matAutocomplete!: MatAutocomplete;
	separatorKeysCodes: number[] = [ENTER, COMMA];
	autocompleteCtrl = new FormControl();
	filteredData: (FilterOption | string)[] = [];
	searchValueUpdate = new Subject<string>();
	params: object = {
		'pagination[start]': 0,
		'pagination[limit]': 15,
		populate: '*',
	};

	constructor(
		private distinctService: DistinctService,
		private elementRef: ElementRef
	) {
	}

	getOptions() {
		if (!this.data.length && this.entityName && this.fieldName) {
			this.params = {
				...this.params,
				filters: { ...this.filterParams },
			};
		}

		this.autocompleteCtrl.valueChanges.pipe(
			startWith(null),
			map((item: string | null) => {
					this.searchValueUpdate.next(item ? item : '');
				}
			)
		).subscribe();

		this.searchValueUpdate
			.pipe(debounceTime(300), distinctUntilChanged(), startWith(null))
			.subscribe((value) => {
				if (this.entityName && this.fieldName) {
					this.params = {
						...this.params,
						filters: {
							...this.filterParams,
							...value ?
								{
									[`${this.fieldName}`]: {
										$containsi: value,
									}
								} : [],
						},
					};

					this.distinctService
						.getDistinctEntity(this.entityName, this.fieldName, {
							params: this.params,
						})
						.pipe(
							tap((data: any) => {
								this.data = (data || []).map((item: any) => {
									const value = item[`${this.fieldName}`];
									return value ? value : '';
								});

								this.filteredData = value ? this._filter(value) : this._filter('')
							})
						)
						.subscribe();
				}
			});
	}

	ngOnInit() {
		this.getOptions();
	}
	focusAutocompleteInput() {
		const autocompleteInput = this.elementRef.nativeElement.querySelector('#autocompleteInput');
		if (autocompleteInput) {
			autocompleteInput.focus();
		}
	}

	add(event: MatChipInputEvent): void {
		let selectedData = [...this.selectedData];

		const input = event.input;
		const value = event.value;

		// Add our value
		if ((value || '').trim()) {
			const isOptionSelected = this.matAutocomplete.options.some((option) => option.selected);
			if (!isOptionSelected) {
				const isDataTypeString = typeof value === 'string';
				if (isDataTypeString) {
					selectedData.push(value.trim());
					this.selectedData = selectedData;

					this.onValueChange.emit({
						value: this.selectedData,
						isMultiSelect: true,
					});
				}
			}
		}

		// Reset the input value
		if (input) {
			input.value = '';
		}

		this.autocompleteCtrl.setValue(null);
	}

	remove(value: string | FilterOption): void {
		let selectedData = [...this.selectedData];
		const chipValue: string = typeof value === 'string' ? value : value.label;
		const index: number = typeof value === 'string' ? selectedData.indexOf(chipValue) : selectedData.findIndex(({ label }: any) => label === chipValue);

		if (index >= 0) {
			selectedData.splice(index, 1);
			this.selectedData = selectedData;

			this.onValueChange.emit({
				value: this.selectedData,
				isMultiSelect: true,
			});
		}
	}

	selected(event: MatAutocompleteSelectedEvent): void {
		event.option.deselect();
		let selectedData = [...this.selectedData];
		const isDataTypeString = typeof this.data[0] === 'string';
		const eventValue = !isDataTypeString ? event.option.value : event.option.viewValue;

		if (!isDataTypeString) {
			const selectedDataValues = [...selectedData.map(({ value, label }: any) => [value, label]).flat()];
			if (!selectedDataValues.includes(eventValue)) {
				const selectedItem = this.data.find(({ value }: any) => value === eventValue);
				if (selectedItem) {
					this.selectedData = [...selectedData, selectedItem];
					this.onValueChange.emit({
						value: this.selectedData,
						isMultiSelect: true,
					});
				}
			}
		} else {
			if (!selectedData.includes(eventValue)) {
				selectedData.push(eventValue);
				this.selectedData = selectedData;

				this.onValueChange.emit({
					value: this.selectedData,
					isMultiSelect: true,
				});
			}
		}

		this.autocompleteInput.nativeElement.value = '';
		this.autocompleteCtrl.setValue(null);
	}

	private _filter(value: string): (FilterOption | string)[] {
		let data: any = [...this.data];
		const filterValue = value.toLowerCase();
		return data.filter((item: FilterOption | string) => {
			const isValueString = typeof item === 'string';
			let itemProp1 = '';
			let itemProp2 = '';

			if (!isValueString) {
				const { value, label } = item || {};
				itemProp1 = label.toLowerCase();
				if (typeof value === 'string') itemProp2 = value.toLowerCase();
			} else {
				itemProp1 = item.toLowerCase();
			}
			return (
				itemProp1.indexOf(filterValue) >= 0 ||
				itemProp2.indexOf(filterValue) >= 0
			);
		});
	}

	addOnBlur(event: FocusEvent) {
		const target: HTMLElement = event.relatedTarget as HTMLElement;
		if (!target || target.tagName !== 'MAT-OPTION') {
			const matChipEvent: MatChipInputEvent | any = {
				input: this.autocompleteInput.nativeElement,
				value: this.autocompleteInput.nativeElement.value,
			};
			this.add(matChipEvent);
		}
	}

	isSelected(item: string | FilterOption) {
		if (typeof item === 'string') {
			return this.selectedData.includes(item);
		} else {
			const selectedDataLabels = this.selectedData.map(({ label, value }: any) => [label, value]).flat();
			return selectedDataLabels.includes(item.label);
		}
	}

	getLabel(value: string | FilterOption): string {
		return typeof value === 'string' ? value : value.label;
	}

	getValue(value: string | FilterOption) {
		return typeof value === 'string' ? value : value.value;
	}
}
